import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../shared/ui/src/components/boxedInputs/textBox';

/*
 * Constants.
 */

export const baseUrl = 'frontkb.com/';

/*
 * Types.
 */

interface FormItem<T> {
  value: T;
  isValid: boolean;
  touched: boolean;
}

export interface BasicFormState {
  name: FormItem<string>;
  url: FormItem<string>;
}

/*
 * Props.
 */

export interface BasicFormProps {
  formState: BasicFormState;
  onChange: HandlerOf<BasicFormState>;
}

/*
 * Styles.
 */

const StyledBasicFormDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const StyledNameTextBox = styled(TextBox)`
  width: 230px;
`;

const StyledUrlTextBox = styled(TextBox)`
  max-width: 575px;
`;

/*
 * Component.
 */

export const BasicForm: FC<BasicFormProps> = ({formState, onChange}) => {
  const onNameChange = (value: string) => {
    onChange({...formState, name: {value, isValid: isNameValid(value), touched: true}});
  };

  const onUrlChange = (value: string) => {
    if (!value.startsWith(baseUrl)) {
      onChange({...formState, url: {value: baseUrl, isValid: false, touched: true}});
    } else {
      onChange({...formState, url: {value, isValid: isUrlValid(value), touched: true}});
    }
  };

  return (
    <StyledBasicFormDiv>
      <StyledNameTextBox
        label={<Trans id="knowledge-base-settings-basic-form-name">Name</Trans>}
        value={formState.name.value}
        onChange={onNameChange}
        isRequired
        hasError={formState.name.touched && !formState.name.isValid}
        errorMessage={<Trans id="knowledge-base-settings-basic-form-name-required">Name is required.</Trans>}
      />
      <StyledUrlTextBox
        label={<Trans id="knowledge-base-settings-basic-form-name">Knowledge base URL</Trans>}
        value={formState.url.value}
        onChange={onUrlChange}
        isRequired
        hasError={formState.url.touched && !formState.url.isValid}
        errorMessage={
          <Trans id="knowledge-base-settings-basic-form-url-error-message">
            Please enter a valid url path.
          </Trans>
        }
      />
    </StyledBasicFormDiv>
  );
};

/*
 * Helpers.
 */

function isNameValid(name: string) {
  return name !== '';
}

function isUrlValid(url: string) {
  const path = url.replace(baseUrl, '');
  const onlyLettersRegex = /^[a-zA-Z]*$/;
  return path !== '' && onlyLettersRegex.test(path);
}
